import { useCallback, useState } from 'react';
import { PausaComMotivo } from '../components/PausaComMotivo';
import { Link, useSearchParams } from 'react-router-dom';
import {
  api,
  ApiError,
  type ObjetivosDeCampanhaResponse,
  type PaidMediaResponse,
  type CampanhaNaTela,
  type PlanoNaTela,
} from '../lib/api';
import { useResource, type Resource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';

/**
 * Mídia paga: o Overview, o assistente de campanha e a lista de campanhas.
 *
 * Fonte: Paid Media Module SPEC v1.0 §5, §6, §12, §13 e §25; Console SPEC v1.5
 * §237 (cards e ações rápidas), §240 a §246 (o assistente), §249 (a lista) e
 * §251 (parar tudo).
 *
 * Esta é a única área do produto em que um botão gasta dinheiro de verdade, e
 * três regras saem daí:
 *
 * 1. **a tela nunca decide.** Ela mostra o que o servidor decidiu e oferece a
 *    ação. O que desabilita um botão aqui é sempre um FATO que o servidor
 *    declarou (não existe provedor conectado neste ambiente), e nunca uma
 *    política que a tela tentou deduzir por conta própria: deduzir política no
 *    cliente é como a tela passa a liberar o que o servidor recusa, ou pior, a
 *    esconder o que ele aceitaria;
 *
 * 2. **ausência tem nome.** Envelope não aprovado não é R$ 0,00, capacidade
 *    nunca validada não é "indisponível" sem mais, e segmento nunca avaliado
 *    não é zero pessoas;
 *
 * 3. **todo número vem pronto do servidor**, dinheiro inclusive. A tela não
 *    formata moeda e não soma nada: duas aritméticas da mesma coisa divergem, e
 *    aqui a divergência é prometer um teto que a reserva não respeita.
 */

const ROTULO_DA_CAPACIDADE: Record<string, string> = {
  paid_distribution: 'distribuir anúncio',
  ad_account_restrictions: 'conta de anúncio saudável',
  campaign_objective_fields: 'catálogo de objetivos',
  custom_audience_upload: 'enviar Custom Audience',
  custom_audience_permissions: 'permissão de Custom Audience',
};

const ROTULO_DO_ESTADO_DA_CAPACIDADE: Record<string, string> = {
  available: 'validada',
  unavailable: 'indisponível',
  unknown: 'nunca validada',
  stale: 'validação vencida',
};

export function PaidMedia() {
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);
  const recurso = useResource<PaidMediaResponse>(() => api.paidMedia(), [versao]);

  /*
    O botão IMPULSIONAR do detalhe de conteúdo (§242) chega aqui com a peça na
    URL. Ler daqui, e não de um estado global, é o que faz o link funcionar
    quando alguém o compartilha ou recarrega a página.
  */
  const [parametros] = useSearchParams();
  const conteudoInicial = parametros.get('conteudo');

  if (recurso.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Mídia paga</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (recurso.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Mídia paga</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar a área de mídia paga.</p>
          <p className="mt-1">{recurso.message}</p>
          <p className="mt-2 text-xs text-ink-faint">
            Nenhum número aparece com zero: zero e &quot;não sabemos&quot; são coisas diferentes, e
            aqui isso decidiria gasto. Código: {recurso.code}
          </p>
        </div>
      </div>
    );
  }

  const dados = recurso.data;

  return (
    <div className="space-y-4 text-sm">
      <header>
        <h1 className="text-2xl font-bold">Mídia paga</h1>
        <p className="mt-1 max-w-3xl text-ink-muted">
          Planos, campanhas e o portão que decide se dinheiro sai. A campanha nasce pausada; ativar
          é um ato separado, e cada ação que gasta pede uma confirmação que diz o valor.
        </p>
      </header>

      <Resumo dados={dados} />
      <Capacidades dados={dados} />
      <Assistente dados={dados} conteudoInicial={conteudoInicial} aoCriar={recarregar} />
      <Planos planos={dados.plans} />
      <Campanhas campanhas={dados.campaigns} dados={dados} aoAtualizar={recarregar} />
    </div>
  );
}

// ===========================================================================
// Resumo (§5, §237)
// ===========================================================================

function Resumo({ dados }: { dados: PaidMediaResponse }) {
  const { module: modulo, envelope, counts, pauses } = dados;
  const ligado = modulo.enabled.valor === true;

  return (
    <section className="mt-6" aria-labelledby="resumo-pago">
      <h2
        id="resumo-pago"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        Situação
      </h2>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Cartao rotulo="Mídia paga">
          <strong>{ligado ? 'ligada' : 'desligada'}</strong>
          {/*
            A frase que distingue uma decisão de uma ausência de decisão. Sem
            ela, "desligada" por default fail-closed e "desligada" porque o dono
            decidiu ficam indistinguíveis, e quem fosse ligar procuraria a coisa
            errada.
          */}
          <p className="mt-1 text-xs text-ink-muted">{modulo.enabled.explicacao}</p>
        </Cartao>

        <Cartao rotulo="Autonomia">
          <strong>{String(modulo.autonomy.valor ?? 'não configurada')}</strong>
          <p className="mt-1 text-xs text-ink-muted">{modulo.autonomy.explicacao}</p>
        </Cartao>

        <Cartao rotulo="Teto declarado no mês">
          {modulo.monthly_cap_declared === null ? (
            <span className="text-ink-faint">não configurado</span>
          ) : (
            <strong>{modulo.monthly_cap_declared.formatado}</strong>
          )}
          <p className="mt-1 text-xs text-ink-muted">{modulo.monthly_cap_explanation}</p>
        </Cartao>

        <Cartao rotulo="Envelope aprovado">
          {envelope.exists ? (
            <>
              <strong>{envelope.available?.formatado}</strong>
              <p className="mt-1 text-xs text-ink-muted">
                de {envelope.limit?.formatado} · já reservado {envelope.used?.formatado}
              </p>
            </>
          ) : (
            <>
              <span className="text-ink-faint">nenhum envelope ativo</span>
              {/*
                Nunca R$ 0,00 aqui. Envelope zerado e envelope inexistente pedem
                ações opostas: no primeiro se espera o mês virar, no segundo
                alguém precisa aprovar um teto.
              */}
              <p className="mt-1 text-xs text-warn-ink">{envelope.reason}</p>
            </>
          )}
        </Cartao>

        <Cartao rotulo="Campanhas gastando agora">
          <strong>{formatNumber(counts.spending)}</strong>
        </Cartao>
        <Cartao rotulo="Campanhas pausadas">
          <strong>{formatNumber(counts.paused)}</strong>
        </Cartao>
        <Cartao rotulo="Planos esperando decisão">
          <strong>{formatNumber(counts.plans_awaiting_approval)}</strong>
        </Cartao>
        <Cartao rotulo="Conexão com a Meta">
          {dados.provider_configured ? (
            <strong>configurada</strong>
          ) : (
            <>
              <span className="text-ink-faint">não configurada</span>
              <p className="mt-1 text-xs text-warn-ink">
                Nenhum código fala com a Meta neste ambiente. Criar e ativar campanha ficam
                desligados, e nada é enviado nem reservado por engano.
              </p>
            </>
          )}
        </Cartao>
      </div>

      {pauses.system === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3">
          <strong>Sistema pausado.</strong> {pauses.system} Nenhuma ação paga acontece enquanto
          isso, e na retomada as campanhas não voltam sozinhas.
        </p>
      )}
      {pauses.paid_media === null ? null : (
        <p className="mt-3 rounded border border-warn-border bg-warn-surface p-3">
          <strong>Mídia paga pausada como categoria.</strong> {pauses.paid_media} O conteúdo
          orgânico continua funcionando.
        </p>
      )}

      <PararTudo pausada={pauses.paid_media !== null} />
    </section>
  );
}

/**
 * PARAR TODA A MÍDIA PAGA (§19, §251), e retomar.
 *
 * Passa pela fronteira de comando que já existe (`PauseCategory` no escopo
 * `paid_media`), e não por uma rota nova: é lá que moram o ator, o motivo, a
 * idempotência e a auditoria, e uma segunda porta para a mesma decisão
 * divergiria da primeira.
 *
 * Não pede confirmação com números, e isso é deliberado: PARAR tem de ser mais
 * fácil do que começar. Atrito aqui é atrito na hora do incidente, e a pausa não
 * gasta nada nem envia nada. O que ela exige é o MOTIVO, que fica no registro.
 *
 * Retomar é o oposto e tem atrito próprio: o servidor pede reautenticação
 * quando a regra exige, e a tela mostra a recusa dele em vez de esconder o
 * botão.
 */
function PararTudo({ pausada }: { pausada: boolean }) {
  return (
    <PausaComMotivo
      pausado={pausada}
      commandType={{ parar: 'PauseCategory', retomar: 'ResumeCategory' }}
      targetRef={{ object_type: 'Category', object_id: 'paid_media' }}
      payload={{ scope: 'paid_media', scope_id: null }}
      rotuloParar="Parar toda a mídia paga"
      rotuloRetomar="Retomar a mídia paga"
      explicacaoAoParar="Nenhuma campanha nova é criada nem ativada enquanto a categoria estiver parada. O conteúdo orgânico continua."
      explicacaoAoRetomar="Retomar a categoria não religa campanha nenhuma sozinha: cada uma volta por decisão de alguém."
      idDoCampo="midia-paga"
    />
  );
}

function Cartao({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-line p-3">
      <p className="text-xs text-ink-muted">{rotulo}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ===========================================================================
// Capacidades (§31 do módulo, §139 do Console)
// ===========================================================================

function Capacidades({ dados }: { dados: PaidMediaResponse }) {
  return (
    <section className="mt-8" aria-labelledby="capacidades-pagas">
      <h2
        id="capacidades-pagas"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        O que a Meta permite hoje
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        Cada linha é uma capacidade separada porque cada uma se resolve de um jeito diferente. Uma
        linha só dizendo &quot;integração indisponível&quot; não diz a quem recorrer.
      </p>
      <ul className="mt-3 space-y-2">
        {dados.capabilities.map((c) => (
          <li key={c.capability} className="rounded border border-line p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">
                {ROTULO_DA_CAPACIDADE[c.capability] ?? c.capability}
              </span>
              <span className={c.state === 'available' ? 'text-xs' : 'text-xs text-warn-ink'}>
                {ROTULO_DO_ESTADO_DA_CAPACIDADE[c.state] ?? c.state}
              </span>
            </div>
            <p className="mt-1 text-xs text-ink-muted">
              {c.provider}/{c.capability} · diagnóstico: {c.status} ·{' '}
              {c.validated_at === null
                ? 'nunca validada'
                : `validada em ${formatInstant(c.validated_at)}`}
            </p>
            {c.state === 'available' ? null : (
              <p className="mt-1 text-xs text-ink-muted">Sem ela: {c.fallback}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ===========================================================================
// O assistente (§240 a §246)
// ===========================================================================

function Assistente({
  dados,
  conteudoInicial,
  aoCriar,
}: {
  dados: PaidMediaResponse;
  conteudoInicial: string | null;
  aoCriar: () => void;
}) {
  const { wizard } = dados;
  const [conteudo, setConteudo] = useState(conteudoInicial ?? wizard.contents[0]?.content_id ?? '');
  const [segmento, setSegmento] = useState(wizard.segments[0]?.segment_id ?? '');
  const [vinculo, setVinculo] = useState('');
  const [objetivo, setObjetivo] = useState('');
  /*
    O catálogo de objetivos da conta, em recurso PRÓPRIO (§244).

    Separado do carregamento da área porque as duas coisas mudam por motivos
    diferentes: planos e campanhas mudam quando alguém age aqui, e o catálogo só
    muda quando alguém sonda a conta de anúncios. E porque a tela precisa
    distinguir os três estados dele, não dois: carregando, sem catálogo (com o
    motivo), e a lista lida.
  */
  const catalogo = useResource<ObjetivosDeCampanhaResponse>(() => api.paidMediaObjetivos(), []);
  const [tipo, setTipo] = useState('DAILY');
  const [valor, setValor] = useState('');
  const [direitos, setDireitos] = useState('UNKNOWN');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [criado, setCriado] = useState<string | null>(null);

  const vinculosDoSegmento = wizard.meta_audience_bindings.filter((v) => v.segment_id === segmento);
  const segmentoEscolhido = wizard.segments.find((s) => s.segment_id === segmento);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setErro(null);
    setCriado(null);

    const numero = Number(valor.replace(',', '.'));
    if (!Number.isFinite(numero) || numero <= 0) {
      setErro('Escreva o valor do orçamento em reais, por exemplo 150,00.');
      return;
    }
    if (objetivo.trim().length === 0) {
      setErro('Escreva o objetivo exatamente como a conta conectada o chama.');
      return;
    }

    setOcupado(true);
    try {
      const r = await api.paidMediaCriarPlano({
        source_content_id: conteudo,
        audience_segment_id: segmento,
        meta_audience_binding_id: vinculo === '' ? undefined : vinculo,
        objective: objetivo.trim(),
        budget_kind: tipo,
        budget_amount: numero,
        rights_paid_permission: direitos,
      });
      setCriado(r.plan.plan_id);
      setValor('');
      aoCriar();
    } catch (e) {
      // A mensagem vem do servidor: é ele que sabe dizer "a capacidade de
      // objetivos não está validada" ou "não há envelope aprovado".
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  if (wizard.contents.length === 0 || wizard.segments.length === 0) {
    return (
      <section className="mt-8" aria-labelledby="assistente">
        <h2
          id="assistente"
          className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
        >
          Montar campanha
        </h2>
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Sem peça publicada ou sem segmento salvo não há o que impulsionar nem para quem. Comece
          por Conteúdo e por Audiência e listas.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-8" aria-labelledby="assistente">
      <h2 id="assistente" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Montar campanha
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        Monta um plano, que ainda não gasta nada. Quem monta não aprova: a aprovação é de outra
        pessoa, e é ela que autoriza o dinheiro a sair.
      </p>

      <form onSubmit={enviar} className="mt-3 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="grow">
            <label htmlFor="peca-do-plano" className="block text-xs text-ink-muted">
              Peça (passo 1)
            </label>
            <select
              id="peca-do-plano"
              value={conteudo}
              onChange={(e) => setConteudo(e.target.value)}
              className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
            >
              {wizard.contents.map((c) => (
                <option key={c.content_id} value={c.content_id}>
                  {c.topic} ({c.lifecycle_state})
                </option>
              ))}
            </select>
            {/*
              A lista NÃO é filtrada por elegibilidade. Quem decide qual estado
              aceita impulsionamento é o portão, num lugar só; repetir a regra
              aqui faria a tela oferecer o que ele recusa, ou esconder o que ele
              aceitaria.
            */}
            <p className="mt-1 text-xs text-ink-faint">
              O estado aparece ao lado. Quem decide se a peça pode ser impulsionada é o portão, no
              detalhe do plano.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="grow">
            <label htmlFor="segmento-do-plano" className="block text-xs text-ink-muted">
              Audiência (passo 2)
            </label>
            <select
              id="segmento-do-plano"
              value={segmento}
              onChange={(e) => {
                setSegmento(e.target.value);
                setVinculo('');
              }}
              className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
            >
              {wizard.segments.map((s) => (
                <option key={s.segment_id} value={s.segment_id}>
                  {s.name} ({s.status})
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-muted">
              {segmentoEscolhido === undefined ? null : segmentoEscolhido.eligible_record_count ===
                null ? (
                <span className="text-ink-faint">
                  nunca avaliado (não é zero: ninguém foi perguntado ainda)
                </span>
              ) : (
                `${formatNumber(segmentoEscolhido.eligible_record_count)} elegíveis após exclusões`
              )}
            </p>
          </div>

          <div className="grow">
            <label htmlFor="vinculo-do-plano" className="block text-xs text-ink-muted">
              Audiência da Meta (opcional)
            </label>
            <select
              id="vinculo-do-plano"
              value={vinculo}
              onChange={(e) => setVinculo(e.target.value)}
              className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
            >
              <option value="">nenhuma (segmentação nativa aprovada)</option>
              {vinculosDoSegmento.map((v) => (
                <option key={v.binding_id} value={v.binding_id}>
                  {v.audience_name} ({v.status}
                  {v.never_synced ? ', nunca sincronizada' : ''})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Objetivo catalogo={catalogo} objetivo={objetivo} aoEscolher={setObjetivo} />

          <div>
            <label htmlFor="tipo-do-orcamento" className="block text-xs text-ink-muted">
              Tipo de orçamento (passo 4)
            </label>
            <select
              id="tipo-do-orcamento"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="mt-1 rounded border border-line px-2 py-1 text-sm"
            >
              <option value="DAILY">por dia</option>
              <option value="LIFETIME">para toda a campanha</option>
            </select>
          </div>

          <div>
            <label htmlFor="valor-do-orcamento" className="block text-xs text-ink-muted">
              Valor em reais
            </label>
            <input
              id="valor-do-orcamento"
              value={valor}
              inputMode="decimal"
              autoComplete="off"
              placeholder="150,00"
              onChange={(e) => setValor(e.target.value)}
              className="mt-1 w-32 rounded border border-line px-2 py-1 text-sm"
            />
            {/*
              Reais, e a conversão para centavos acontece no SERVIDOR. Unidade
              na fronteira é onde dinheiro erra por 100x.
            */}
            <p className="mt-1 text-xs text-ink-faint">o servidor converte para centavos</p>
          </div>

          <div>
            <label htmlFor="direitos-do-plano" className="block text-xs text-ink-muted">
              Direito de uso em mídia paga
            </label>
            <select
              id="direitos-do-plano"
              value={direitos}
              onChange={(e) => setDireitos(e.target.value)}
              className="mt-1 rounded border border-line px-2 py-1 text-sm"
            >
              <option value="UNKNOWN">não registrado</option>
              <option value="GRANTED">concedido</option>
              <option value="DENIED">negado</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={ocupado}
          className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          Criar plano
        </button>
      </form>

      {erro === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-sm">
          {erro}
        </p>
      )}
      {criado === null ? null : (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
          Plano criado e esperando decisão.{' '}
          <Link className="underline" to={`/aquisicao/planos/${criado}`}>
            Ver o plano e o portão
          </Link>
        </p>
      )}
    </section>
  );
}

/**
 * O objetivo da campanha (Console SPEC §244).
 *
 * Duas formas, e a escolha entre elas é do SERVIDOR, não da tela: quando ele
 * devolve a lista lida da conta, aqui vira um seletor; quando não há lista, o
 * campo digitado continua existindo com o motivo escrito ao lado.
 *
 * O motivo escrito é a parte que não pode faltar. Um campo de texto sem
 * explicação faz a pessoa achar que digitar é o normal e escrever o nome que
 * lhe parece certo, e aí o plano nasce com um objetivo que a conta não aceita.
 *
 * E nunca um seletor vazio no lugar de "não há catálogo": lista vazia e ausência
 * de lista pedem ações opostas. A primeira é conta sondada que não habilita
 * objetivo nenhum, e não há o que fazer aqui; a segunda é conta por conectar.
 */
function Objetivo({
  catalogo,
  objetivo,
  aoEscolher,
}: {
  catalogo: Resource<ObjetivosDeCampanhaResponse>;
  objetivo: string;
  aoEscolher: (valor: string) => void;
}) {
  const rotulo = (
    <label htmlFor="objetivo-do-plano" className="block text-xs text-ink-muted">
      Objetivo (passo 3)
    </label>
  );

  if (catalogo.kind === 'loading') {
    return (
      <div className="grow" aria-busy="true">
        {rotulo}
        <p className="mt-1 text-xs text-ink-muted">Lendo o catálogo de objetivos da conta…</p>
      </div>
    );
  }

  /*
    Erro ao ler o catálogo NÃO vira campo digitado silencioso: quem digitasse
    aqui acharia que está no caminho normal, e o plano nasceria com um objetivo
    que ninguém conferiu contra a conta. O campo aparece, e o aviso diz que o
    sistema não conseguiu ler a lista.
  */
  const semLista =
    catalogo.kind === 'error'
      ? `Não foi possível ler o catálogo de objetivos da conta (${catalogo.message}). Confira o nome com quem administra a conta antes de escrever.`
      : catalogo.data.objectives === null
        ? catalogo.data.reason
        : null;

  if (semLista !== null) {
    return (
      <div className="grow">
        {rotulo}
        <input
          id="objetivo-do-plano"
          value={objetivo}
          autoComplete="off"
          onChange={(e) => aoEscolher(e.target.value)}
          className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
        />
        <p className="mt-1 text-xs text-warn-ink">
          {semLista} Enquanto isso, escreva exatamente como a conta conectada o chama: o portão
          confere a palavra contra o catálogo dela, e recusa o que não estiver lá.
        </p>
      </div>
    );
  }

  const opcoes = catalogo.kind === 'ready' ? (catalogo.data.objectives ?? []) : [];
  const versao = catalogo.kind === 'ready' ? catalogo.data.capability_version : null;

  return (
    <div className="grow">
      {rotulo}
      <select
        id="objetivo-do-plano"
        value={objetivo}
        onChange={(e) => aoEscolher(e.target.value)}
        className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
      >
        {/*
          A opção vazia existe para a escolha ser DELIBERADA. Pré-selecionar o
          primeiro objetivo da conta faria o plano nascer com uma escolha que
          ninguém fez, e o objetivo decide para que a Meta otimiza o dinheiro.
        */}
        <option value="">escolha o objetivo</option>
        {opcoes.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {opcoes.length === 0 ? (
        <p className="mt-1 text-xs text-warn-ink">
          A conta conectada foi consultada e não habilita objetivo nenhum. Não há o que escolher
          aqui, e escrever um nome à mão não mudaria isso: quem resolve é quem administra a conta.
        </p>
      ) : (
        <p className="mt-1 text-xs text-ink-muted">
          {/*
            A procedência aparece junto da lista. Sem ela a pessoa não sabe se
            está escolhendo de um catálogo lido hoje ou de um de três meses
            atrás, e o §24 trata a procedência do rótulo como parte do rótulo.
          */}
          Lido da conta conectada{versao === null ? '' : ` sob ${versao}`}. Nenhum nome desta lista
          foi escrito por nós.
        </p>
      )}
    </div>
  );
}

// ===========================================================================
// Planos (§248)
// ===========================================================================

function Planos({ planos }: { planos: PlanoNaTela[] }) {
  return (
    <section className="mt-8" aria-labelledby="planos-pagos">
      <h2
        id="planos-pagos"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        Planos
      </h2>

      {planos.length === 0 ? (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhum plano montado ainda. Um plano é uma proposta: ele não gasta nada até alguém aprovar
          e criar a campanha.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {planos.map((p) => (
            <li key={p.plan_id} className="rounded-card border border-line bg-card p-4 shadow-card">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <Link className="font-medium underline" to={`/aquisicao/planos/${p.plan_id}`}>
                  {p.content?.topic ?? 'peça não encontrada'}
                </Link>
                <span className="text-xs text-ink-muted">{p.status_label}</span>
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                {p.budget.formatado} {p.budget_kind_label} · audiência{' '}
                {p.audience?.name ?? 'não encontrada'} · direito de uso{' '}
                {p.rights_paid_permission_label}
              </p>
              <p className="mt-1 text-xs text-ink-faint">
                Proposto por {p.created_by_agent} em {formatInstant(p.created_at)}
                {p.approved_by === null ? '' : ` · aprovado por ${p.approved_by}`}
              </p>
              {p.governance.pending ? (
                <p className="mt-2 rounded border border-warn-border bg-warn-surface p-2 text-xs">
                  A governança precisa ser reavaliada antes desta peça circular paga. O que mudou:{' '}
                  {p.governance.triggers.join(', ')}.
                </p>
              ) : null}
              {p.campaigns.length === 0 ? null : (
                <p className="mt-2 text-xs text-ink-muted">
                  {formatNumber(p.campaigns.length)} campanha(s) criada(s) a partir dele.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===========================================================================
// Campanhas (§249, §250, §13)
// ===========================================================================

function Campanhas({
  campanhas,
  dados,
  aoAtualizar,
}: {
  campanhas: CampanhaNaTela[];
  dados: PaidMediaResponse;
  aoAtualizar: () => void;
}) {
  const [pausando, setPausando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function pausar(id: string) {
    if (motivo.trim().length < 3) {
      setAviso('Escreva o motivo da pausa. Ele fica no registro da campanha.');
      return;
    }
    setOcupado(true);
    setAviso(null);
    try {
      await api.paidMediaPausarCampanha(id, { reason: motivo.trim() });
      setAviso('Campanha pausada. O estado efetivo do provedor foi lido logo depois.');
      setPausando(null);
      setMotivo('');
      aoAtualizar();
    } catch (e) {
      setAviso(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="mt-8" aria-labelledby="campanhas-pagas">
      <h2
        id="campanhas-pagas"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        Campanhas
      </h2>

      {aviso === null ? null : (
        <p className="mt-2 rounded-[11px] border border-line bg-surface-sunken p-3 text-sm">
          {aviso}
        </p>
      )}

      {campanhas.length === 0 ? (
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhuma campanha criada. Nada está gastando.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {campanhas.map((c) => (
            <li
              key={c.campaign_id}
              className="rounded-card border border-line bg-card p-4 shadow-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{c.name}</span>
                <span className={c.spends_money ? 'text-xs font-medium' : 'text-xs text-ink-muted'}>
                  {c.requested_state_label}
                </span>
              </div>

              <p className="mt-1 text-xs text-ink-muted">
                {c.budget.formatado} de orçamento · gasto {c.spent.formatado} · objetivo{' '}
                {c.objective}
              </p>

              {/*
                §13: o estado que NÓS pedimos e o que o provedor RESPONDE, lado a
                lado. Nunca reconciliada é um estado próprio: um traço aqui seria
                lido como concordância, e concordância é exatamente o que
                ninguém verificou ainda.
              */}
              <p className="mt-1 text-xs text-ink-muted">
                Pedimos: <strong>{c.requested_state_in_spec}</strong> · A Meta responde:{' '}
                {c.never_reconciled ? (
                  <span className="text-ink-faint">nunca perguntamos</span>
                ) : (
                  <strong>{c.provider_effective_state}</strong>
                )}
                {c.last_provider_sync_at === null
                  ? ''
                  : ` (lido em ${formatInstant(c.last_provider_sync_at)})`}
              </p>

              {c.provider_state_diverges ? (
                <p className="mt-2 rounded border border-danger-border bg-danger-surface p-2 text-xs">
                  O estado na Meta não é o que pedimos. Alguém pode ter mexido pelo painel dela.
                  Confira antes de qualquer outra ação nesta campanha.
                </p>
              ) : null}

              {c.blocked_reason === null ? null : (
                <p className="mt-2 rounded border border-warn-border bg-warn-surface p-2 text-xs">
                  {c.blocked_reason}
                </p>
              )}

              {c.spends_money && dados.provider_configured ? (
                pausando === c.campaign_id ? (
                  <div className="mt-3 rounded border border-line p-3">
                    <label
                      htmlFor={`motivo-da-pausa-${c.campaign_id}`}
                      className="block text-xs text-ink-muted"
                    >
                      Motivo da pausa
                    </label>
                    <input
                      id={`motivo-da-pausa-${c.campaign_id}`}
                      value={motivo}
                      autoComplete="off"
                      onChange={(e) => setMotivo(e.target.value)}
                      className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => pausar(c.campaign_id)}
                        disabled={ocupado}
                        className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-xs text-white disabled:opacity-50"
                      >
                        Pausar campanha
                      </button>
                      <button
                        type="button"
                        onClick={() => setPausando(null)}
                        disabled={ocupado}
                        className="rounded border border-line-strong px-3 py-1 text-xs hover:bg-surface-raised disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setPausando(c.campaign_id);
                      setAviso(null);
                    }}
                    className="mt-2 rounded border border-line px-2 py-1 text-xs hover:bg-surface-raised"
                  >
                    Pausar
                  </button>
                )
              ) : null}

              {c.plan_id === null ? null : (
                <p className="mt-2 text-xs">
                  <Link className="underline" to={`/aquisicao/planos/${c.plan_id}`}>
                    Ver o plano e o portão desta campanha
                  </Link>
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
